import React, { useState, useEffect, useCallback, useRef } from 'react';
import axiosInstance from '../../api/axiosInstance';
import Editor from '@monaco-editor/react';
import { toast } from 'react-toast';
import { useLoading } from '../../context/LoadingContext';

// Suppress ResizeObserver errors
// const originalError = window.console.error;
// window.console.error = (...args) => {
//   if (/ResizeObserver/.test(args[0])) return;
//   originalError.apply(console, args);
// };
import '../../styles/common.css';
import '../../styles/test-suites.css';
import CreateTestSuiteModal from './CreateTestSuiteModal';
import CreateTestCaseModal from './CreateTestCaseModal';
import AddAITestCasesModal from './AddAITestCasesModal';
import { DraggableList } from '../common';
import { useSearchParams, Navigate, useNavigate } from 'react-router';
import { dateFormat } from '../../utils/dateFormat';
import ViewTestCaseModal from './ViewTestCaseModal';
import EditTestCaseModal from './EditTestCaseModal';
import ConfirmRegenerateModal from '../common/ConfirmRegenerateModal';
import RunTestCaseModal from './RunTestCaseModal';
import RunTestSuiteModal from './RunTestSuiteModal';
import { formatDate as formatUserDate } from '../../utils/dateTimeFormat';
// import ViewScriptModal from '../common/ViewScriptModal';



const TestSuitesList = ({ projectId, onBack, selectedTestSuite }) => {
  const { startLoading, stopLoading } = useLoading();
  const [testSuites, setTestSuites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTestSuite, setActiveTestSuite] = useState(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isCreateTestCaseModalOpen, setIsCreateTestCaseModalOpen] = useState(false);
  const [isAddAITestCasesModalOpen, setIsAddAITestCasesModalOpen] = useState(false);
  const [projects, setProjects] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState("");
  const [actionFeedback, setActionFeedback] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const [testSuitForSelectedProj, setTestSuitForSelectedProj]= useState({})
  const [testCases, setTestCases] = useState([]);
  const suiteIdFromUrl = searchParams.get('suite_id');
  const [suiteId, setSuiteId] = useState(suiteIdFromUrl || null);
  const fetchTestCasesRequestIdRef = useRef(0);
  const [isReorderMode, setIsReorderMode] = useState(false);
  const [reorderedTestCases, setReorderedTestCases] = useState([]);
  const [isReorderTestSuiteMode, setIsReorderTestSuiteMode] = useState(false);
  const [reorderedTestSuites, setReorderedTestSuites] = useState([]);
  const [isViewTestCaseModalOpen, setIsViewTestCaseModalOpen] = useState(false);
  const [isEditTestCaseModalOpen, setIsEditTestCaseModalOpen] = useState(false);
  const [selectedTestCase, setSelectedTestCase] = useState(null);
  const [isViewScriptModalOpen, setIsViewScriptModalOpen] = useState(false);
  const [editorContent, setEditorContent] = useState('');
  const [isSavingScript, setIsSavingScript] = useState(false);
  
  // Confirm regenerate modal state
  const [isConfirmRegenerateModalOpen, setIsConfirmRegenerateModalOpen] = useState(false);
  const [testCaseToRegenerate, setTestCaseToRegenerate] = useState(null);
  const [isRegeneratingScript, setIsRegeneratingScript] = useState(false);
  
  // Pagination state
  const [totalCount, setTotalCount] = useState(0);
  const pageSize = 10;
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMoreSuites, setHasMoreSuites] = useState(false);
  const [isFetchingMoreSuites, setIsFetchingMoreSuites] = useState(false);
  const testSuitesListRef = useRef(null);
  const loadMoreTriggerRef = useRef(null);
  const isFetchingSuitesRef = useRef(false);

  // Test cases pagination state
  const [testCasesTotalCount, setTestCasesTotalCount] = useState(0);
  const [testCasesCurrentPage, setTestCasesCurrentPage] = useState(1);
  const [hasMoreTestCases, setHasMoreTestCases] = useState(false);
  const [isFetchingMoreTestCases, setIsFetchingMoreTestCases] = useState(false);
  const [isLoadingTestCases, setIsLoadingTestCases] = useState(false);
  const testCasesScrollRef = useRef(null);
  const loadMoreTestCasesTriggerRef = useRef(null);
  const isFetchingTestCasesRef = useRef(false);

  // Run Test Case Modal state
  const [isRunTestCaseModalOpen, setIsRunTestCaseModalOpen] = useState(false);
  const [selectedTestCaseForRun, setSelectedTestCaseForRun] = useState(null);

  // Run Test Suite Modal state
  const [isRunTestSuiteModalOpen, setIsRunTestSuiteModalOpen] = useState(false);

  // Test Suite execution state
  const [isExecutingTestSuite, setIsExecutingTestSuite] = useState(false);
  const [testSuiteExecutionResults, setTestSuiteExecutionResults] = useState(null);

  // ── postMessage listener for test suite execution ───────────────────────
  useEffect(() => {
    const handler = (event) => {
      const msg = event.data;
      if (msg?.source !== "ROBITEST_AGENT") return;

      if (msg.type === "TEST_SUITES_TRIGGERED") {
        setIsExecutingTestSuite(false);
        
        if (msg.success) {
          setTestSuiteExecutionResults(msg);
          toast.success("Test suite execution started successfully!");
          // You can add additional handling here, like opening a results modal
        } else {
          toast.error(msg.error || "Test suite execution failed");
          setTestSuiteExecutionResults(msg);
        }
      }
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, []);

  const handleGenerateTestCase = async (testCaseId) => {
    // Find the test case to check if it needs regeneration
    const testCase = testCases.find(tc => tc.test_case_id === testCaseId);
    
    // If script_status is true, show confirmation modal
    if (testCase && testCase.script_status) {
      setTestCaseToRegenerate({ id: testCaseId, name: testCase.test_case_name });
      setIsConfirmRegenerateModalOpen(true);
      return;
    }
    
    // Otherwise, proceed with generation
    await executeGenerateTestCase(testCaseId);
  };

  /**
   * Execute the actual generation/regeneration of test case script
   * Uses global loader from context
   */
  const executeGenerateTestCase = async (testCaseId) => {
    startLoading();
    setIsRegeneratingScript(true);
    
    try {
      const response = await axiosInstance.post(`/execute_file/generate_script/${testCaseId}`);
      if (response.status == 'success') {
        showActionFeedback(
          `${response.message} - Test Case ID: ${response.test_case_id}`, 
          'success'
        );
        handleViewScript(testCaseId);
        
        // Refresh test cases list to show updated status
        await fetchTestCases(undefined, 1, true);
      } else {
        throw new Error(response.message || 'Failed to generate automation script');
      }
    } catch (error) {
      console.error('Error generating test case script:', error);
      showActionFeedback(
        error.response?.message || 'Failed to generate automation script', 
        'error'
      );
    } finally {
      stopLoading();
      setIsRegeneratingScript(false);
      setIsConfirmRegenerateModalOpen(false);
    }
  };

  /**
   * Handle confirm regeneration
   */
  const handleConfirmRegenerate = async () => {
    if (testCaseToRegenerate) {
      await executeGenerateTestCase(testCaseToRegenerate.id);
    }
  };

  const handleViewScript = async (testCase) => {
    setSelectedTestCase(testCase);
    setIsViewScriptModalOpen(true);
    try {
      const response = await axiosInstance.get(`/execute_file/get_automation_script/${testCase.test_case_id || testCase}`);
      setEditorContent(response || '// No script available');
    } catch (error) {
      console.error('Error fetching automation script:', error);
      setEditorContent('// Error loading automation script');
      showActionFeedback('Failed to load automation script', 'error');
    }
  };

  const handleSaveScript = async () => {
    if ( !selectedTestCase && !selectedTestCase?.test_case_id  ) {
      showActionFeedback('No test case selected', 'error');
      return;
    }

    setIsSavingScript(true);
    try {
      const response = await axiosInstance.put(
        `/execute_file/test-cases/${selectedTestCase.test_case_id || selectedTestCase}/update_script`,
        editorContent,
        {
          headers: {
            'Content-Type': 'text/plain'
          }
        }
      );

      if (response.status == 'success' || response.status === 200) {
        showActionFeedback('Script saved successfully!', 'success');
        setIsViewScriptModalOpen(false);
      } else {
        throw new Error(response.message || 'Failed to save script');
      }
    } catch (error) {
      console.error('Error saving script:', error);
      showActionFeedback(
        error.response?.data?.message || error.message || 'Failed to save script', 
        'error'
      );
    } finally {
      setIsSavingScript(false);
    }
  };

  //  Generate all test cases for a project
  const handleGenerateAllTestCases = async () => {
    toast.success("Generating automation scripts for all test cases in this project...");

    if (!selectedProjectId) {
      showActionFeedback('Please select a project first', 'error');
      return;
    }

const response =  {
    "status": "success",
    "message": "Automation script generation triggered for 1",
    "task_status": "in_progress"
}

    try {
      // const response = await axiosInstance.post(`/execute_file/generate_project_scripts_bg/${selectedProjectId}`);

      if (response.status === 'success') {
        // toast.success(response.message || 'Automation script generation triggered for 1 test case');

        // showActionFeedback(
        //   `${response.message} - Status: ${response.task_status}`, 
        //   'success'
        // );
      } else {
        throw new Error(response.message || 'Failed to trigger automation script generation');
      }
    } catch (error) {
      console.error('Error generating all test cases:', error);
      showActionFeedback(
        error.response?.message || 'Failed to generate automation scripts for project', 
        'error'
      );
    }
  };

  const getEffectiveSuiteId = useCallback((overrideSuiteId) => {
    return (
      overrideSuiteId ??
      suiteId ??
      searchParams.get('suite_id') ??
      activeTestSuite?.suite_id ??
      null
    );
  }, [suiteId, searchParams, activeTestSuite?.suite_id]);

  const fetchTestCases = useCallback(async (overrideSuiteId, page = 1, shouldReset = false) => {
    if (!selectedProjectId) {
      setTestCases([]);
      return;
    }

    if (isFetchingTestCasesRef.current) return;

    const effectiveSuiteId = getEffectiveSuiteId(overrideSuiteId);
    const requestId = ++fetchTestCasesRequestIdRef.current;

    isFetchingTestCasesRef.current = true;
    if (page === 1 || shouldReset) {
      setIsLoadingTestCases(true);
    } else {
      setIsFetchingMoreTestCases(true);
    }

    try {
      const response = effectiveSuiteId
        ? await axiosInstance.get('/test_case/suite_cases', {
            params: {
              suite_id: effectiveSuiteId,
              page,
              page_size: pageSize,
            },
          })
        : await axiosInstance.get('/test_case/cases_list', {
            params: {
              project_id: selectedProjectId,
              page,
              page_size: pageSize,
            },
          });

      if (requestId !== fetchTestCasesRequestIdRef.current) return;

      const incomingCases = response.data || [];
      const total = response.total || 0;
      const totalPages = Math.max(1, Math.ceil(total / pageSize));
      const nextHasMore = page < totalPages;

      setTestCasesTotalCount(total);
      setTestCasesCurrentPage(page);
      setHasMoreTestCases(nextHasMore);

      setTestCases((prevCases) => {
        if (page === 1 || shouldReset) {
          return incomingCases;
        }

        const existingIds = new Set(prevCases.map((tc) => tc.test_case_id));
        const uniqueIncomingCases = incomingCases.filter((tc) => !existingIds.has(tc.test_case_id));
        return [...prevCases, ...uniqueIncomingCases];
      });
    } catch {
      if (requestId !== fetchTestCasesRequestIdRef.current) return;
      if (page === 1 || shouldReset) {
        setTestCases([]);
        setTestCasesTotalCount(0);
        setHasMoreTestCases(false);
      }
    } finally {
      if (requestId === fetchTestCasesRequestIdRef.current) {
        setIsLoadingTestCases(false);
        setIsFetchingMoreTestCases(false);
        isFetchingTestCasesRef.current = false;
      }
    }
  }, [selectedProjectId, getEffectiveSuiteId, pageSize]);

  const selectTestSuite = useCallback((suite) => {
    setActiveTestSuite(suite);
    setSuiteId(suite.suite_id);
    setIsReorderMode(false);
    setReorderedTestCases([]);
    setTestCases([]);
    setTestCasesCurrentPage(1);
    setHasMoreTestCases(false);
    isFetchingTestCasesRef.current = false;
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('suite_id', String(suite.suite_id));
        return next;
      },
      { replace: true }
    );
  }, [setSearchParams]);

  // Set selectedProjectId from props
  useEffect(() => {
    if (projectId) {
      setSelectedProjectId(projectId);
    }
  }, [projectId]);

  useEffect(() => {
    if (!selectedProjectId) {
      setTestCases([]);
      setTestCasesTotalCount(0);
      setTestCasesCurrentPage(1);
      setHasMoreTestCases(false);
      return;
    }

    setTestCases([]);
    setTestCasesCurrentPage(1);
    setHasMoreTestCases(false);
    isFetchingTestCasesRef.current = false;
    fetchTestCases(undefined, 1, true);
  }, [selectedProjectId, suiteId, suiteIdFromUrl, activeTestSuite?.suite_id, fetchTestCases]);

  const fetctTestSuite = useCallback(async (projectId, page = 1, shouldReset = false) => {
    if (!projectId || isFetchingSuitesRef.current) return;

    isFetchingSuitesRef.current = true;
    if (page === 1 || shouldReset) {
      setLoading(true);
    } else {
      setIsFetchingMoreSuites(true);
    }
    setError(null);

    try {
      const response = await axiosInstance.get(`/test_suite/list/${projectId}`, {
        params: {
          page,
          page_size: pageSize,
          sort_by: 2,
          sort_order: 1,
        },
      });

      const incomingSuites = response.test_suites || [];
      const totalPagesFromApi = response.total_pages || 1;
      const nextHasMore = page < totalPagesFromApi;

      setTotalCount(response.total_count || 0);
      setCurrentPage(page);
      setHasMoreSuites(nextHasMore);

      setTestSuites((prevSuites) => {
        if (page === 1 || shouldReset) {
          return incomingSuites;
        }

        const existingIds = new Set(prevSuites.map((suite) => suite.suite_id));
        const uniqueIncomingSuites = incomingSuites.filter((suite) => !existingIds.has(suite.suite_id));
        return [...prevSuites, ...uniqueIncomingSuites];
      });

      if (page === 1 && incomingSuites.length === 0) {
        setActiveTestSuite(null);
      }
    } catch {
      setError('Failed to load test suites. Please try again later.');
    } finally {
      setLoading(false);
      setIsFetchingMoreSuites(false);
      isFetchingSuitesRef.current = false;
    }
  }, [pageSize]);

  useEffect(() => {
    if (!selectedProjectId) return;

    setTestSuites([]);
    setCurrentPage(1);
    setHasMoreSuites(false);
    fetctTestSuite(selectedProjectId, 1, true);
  }, [selectedProjectId, fetctTestSuite]);

  useEffect(() => {
    if (!selectedProjectId || isReorderTestSuiteMode || loading || isFetchingMoreSuites || !hasMoreSuites) {
      return;
    }

    const observerTarget = loadMoreTriggerRef.current;
    const listContainer = testSuitesListRef.current;
    if (!observerTarget || !listContainer) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const firstEntry = entries[0];
        if (firstEntry?.isIntersecting && !isFetchingSuitesRef.current) {
          fetctTestSuite(selectedProjectId, currentPage + 1);
        }
      },
      {
        root: listContainer,
        rootMargin: '80px',
        threshold: 0,
      }
    );

    observer.observe(observerTarget);
    return () => observer.disconnect();
  }, [selectedProjectId, isReorderTestSuiteMode, loading, isFetchingMoreSuites, hasMoreSuites, currentPage, fetctTestSuite]);

  // Auto-select suite when suiteId is provided and testSuites are loaded
  useEffect(() => {
    if (suiteId && testSuites.length > 0) {
      const targetSuite = testSuites.find(suite => suite.suite_id.toString() === suiteId.toString());
      if (targetSuite) {
        setActiveTestSuite(targetSuite);
      }
    }
  }, [suiteId, testSuites]);

  const fetchAllTestSuitesForReorder = useCallback(async (existingSuites = [], startPage = 1) => {
    if (!selectedProjectId) return existingSuites;

    const allSuites = [...existingSuites];
    const seenSuiteIds = new Set(allSuites.map((suite) => suite.suite_id));
    let page = startPage;
    let totalPagesFromApi = Math.max(1, Math.ceil(totalCount / pageSize));

    while (page <= totalPagesFromApi) {
      const response = await axiosInstance.get(`/test_suite/list/${selectedProjectId}`, {
        params: {
          page,
          page_size: pageSize,
          sort_by: 2,
          sort_order: 1,
        },
      });

      totalPagesFromApi = response.total_pages || totalPagesFromApi;
      (response.test_suites || []).forEach((suite) => {
        if (!seenSuiteIds.has(suite.suite_id)) {
          seenSuiteIds.add(suite.suite_id);
          allSuites.push(suite);
        }
      });
      page += 1;
    }

    setTestSuites(allSuites);
    setCurrentPage(totalPagesFromApi);
    setHasMoreSuites(false);

    return allSuites;
  }, [selectedProjectId, pageSize, totalCount]);

  const fetchAllTestCasesForReorder = useCallback(async (existingCases = [], startPage = 1, overrideSuiteId) => {
    const effectiveSuiteId = getEffectiveSuiteId(overrideSuiteId);
    if (!selectedProjectId) return existingCases;

    const allCases = [...existingCases];
    const seenCaseIds = new Set(allCases.map((tc) => tc.test_case_id));
    let page = startPage;
    let totalPages = Math.max(1, Math.ceil(testCasesTotalCount / pageSize));

    while (page <= totalPages) {
      const response = effectiveSuiteId
        ? await axiosInstance.get('/test_case/suite_cases', {
            params: {
              suite_id: effectiveSuiteId,
              page,
              page_size: pageSize,
            },
          })
        : await axiosInstance.get('/test_case/cases_list', {
            params: {
              project_id: selectedProjectId,
              page,
              page_size: pageSize,
            },
          });

      const total = response.total ?? testCasesTotalCount;
      totalPages = Math.max(1, Math.ceil(total / pageSize));
      (response.data || []).forEach((tc) => {
        if (!seenCaseIds.has(tc.test_case_id)) {
          seenCaseIds.add(tc.test_case_id);
          allCases.push(tc);
        }
      });
      page += 1;
    }

    setTestCases(allCases);
    setTestCasesCurrentPage(totalPages);
    setHasMoreTestCases(false);
    setTestCasesTotalCount(allCases.length);

    return allCases;
  }, [selectedProjectId, getEffectiveSuiteId, pageSize, testCasesTotalCount]);

  useEffect(() => {
    if (!selectedProjectId || isReorderMode || isLoadingTestCases || isFetchingMoreTestCases || !hasMoreTestCases) {
      return;
    }

    const observerTarget = loadMoreTestCasesTriggerRef.current;
    const listContainer = testCasesScrollRef.current;
    if (!observerTarget || !listContainer) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const firstEntry = entries[0];
        if (firstEntry?.isIntersecting && !isFetchingTestCasesRef.current) {
          fetchTestCases(undefined, testCasesCurrentPage + 1);
        }
      },
      {
        root: listContainer,
        rootMargin: '80px',
        threshold: 0,
      }
    );

    observer.observe(observerTarget);
    return () => observer.disconnect();
  }, [
    selectedProjectId,
    isReorderMode,
    isLoadingTestCases,
    isFetchingMoreTestCases,
    hasMoreTestCases,
    testCasesCurrentPage,
    fetchTestCases,
  ]);


  //   if (!projectId) {
  //   return <Navigate to="test-suites" replace />;
  // }

  // Function to show action feedback
  const showActionFeedback = (message, type = 'success') => {
    setActionFeedback({ message, type });
    
    // Auto-hide after 3 seconds
    setTimeout(() => {
      setActionFeedback(null);
    }, 3000);
  };
  
  // Function to handle creating a new test suite
  const handleCreateTestSuite = (newTestSuite) => {
    // In a real application, this would make an API call to create the test suite
    // For this example, we'll just add it to the local state
    setTestSuites([...testSuites, newTestSuite]);
    
    // Set the new test suite as active
    setActiveTestSuite(newTestSuite);
    
    // Show success message
    showActionFeedback(`Test suite "${newTestSuite.name}" created successfully`, 'success');
  };
  
  // Function to handle creating a new test case
  // const handleCreateTestCase = (newTestCase) => {
  //   // In a real application, this would make an API call to create the test case
  //   // For this example, we'll just add it to the active test suite's test cases
  //   if (activeTestSuite) {
  //     // Create a copy of the active test suite
  //     const updatedTestSuite = {
  //       ...activeTestSuite,
  //       testCases: [...activeTestSuite.testCases, newTestCase],
  //       updatedAt: new Date().toISOString()
  //     };
      
  //     // Update the test suites array with the updated test suite
  //     const updatedTestSuites = testSuites.map(suite =>
  //       suite.id === activeTestSuite.id ? updatedTestSuite : suite
  //     );
      
  //     // Update state
  //     setTestSuites(updatedTestSuites);
  //     setActiveTestSuite(updatedTestSuite);
      
  //     // Show success message
  //     showActionFeedback(`Test case "${newTestCase.name}" added successfully`, 'success');
  //   }
  // };

  const handleCreateTestCase = async (newTestCase) => {
  try {
    // After successful creation, refresh the test cases list
    if (activeTestSuite) {
      await fetchTestCases(activeTestSuite.suite_id, 1, true);
    } else if (selectedProjectId) {
      // If no active suite, fetch all test cases for project
      await fetchTestCases(undefined, 1, true);
    }
    
    // Show success message
    showActionFeedback(`Test case "${newTestCase.name}" created successfully`, 'success');
    // Close the modal
    setIsCreateTestCaseModalOpen(false);
  } catch (error) {
    showActionFeedback('Failed to create test case. Please try again.', 'error');
  }
};
  
  // Function to handle adding AI-generated test cases
  const handleAddAITestCases = (testCases) => {
    if (activeTestSuite) {
      // Create a copy of the active test suite
      const updatedTestSuite = {
        ...activeTestSuite,
        testCases: [...activeTestSuite.testCases, ...testCases],
        updatedAt: new Date().toISOString()
      };
      
      // Update the test suites array with the updated test suite
      const updatedTestSuites = testSuites.map(suite => 
        suite.id === activeTestSuite.id ? updatedTestSuite : suite
      );
      
      // Update state
      setTestSuites(updatedTestSuites);
      setActiveTestSuite(updatedTestSuite);
      
      // Show success message
      showActionFeedback(`${testCases.length} AI-generated test cases added successfully`, 'success');
    }
  };
  
  // Function to handle reordering test cases
  // OLD IMPLEMENTATION: Manual save button approach
  // const handleReorderTestCase = async () => {
  //   if (isReorderMode) {
  //     try {
  //       const payload = {
  //         project_id: selectedProjectId,
  //         suite_id: activeTestSuite?.suite_id,
  //         order: reorderedTestCases.map(tc => tc.test_case_id)
  //       };

  //       const response = await axiosInstance.put('/test_case/reorder_cases', payload);

  //       if (response.status === 'success') {
  //         // Update the test cases state with the new order
  //         setTestCases(reorderedTestCases);
  //         setIsReorderMode(false);
  //         showActionFeedback(response.data.message || 'Test cases reordered successfully', 'success');
  //       }
  //     } catch (error) {
  //       showActionFeedback(error.response?.data?.message || 'Failed to reorder test cases', 'error');
  //     }
 
  




  // };

  // NEW IMPLEMENTATION: Auto-save on drop functionality
  const handleReorderTestCase = async () => {
    let casesToReorder = [...testCases];
    const hasAllCasesLoaded = !hasMoreTestCases && testCases.length >= testCasesTotalCount;

    if (!hasAllCasesLoaded) {
      const requestId = `load-all-test-cases-for-reorder-${Date.now()}`;
      startLoading(requestId);
      try {
        casesToReorder = await fetchAllTestCasesForReorder(
          testCases,
          testCasesCurrentPage + 1,
          activeTestSuite?.suite_id
        );
      } catch {
        toast.error('Failed to load all test cases for reordering');
        return;
      } finally {
        stopLoading(requestId);
      }
    }

    setReorderedTestCases(casesToReorder);
    setIsReorderMode(true);
  };

  // Auto-save function called when test case order changes
  const handleTestCaseOrderChange = async (newOrderedTestCases) => {
    const requestId = `reorder-test-cases-${Date.now()}`;
    startLoading(requestId);
    
    const originalTestCases = [...reorderedTestCases];
    
    try {
      // Optimistically update the UI
      setReorderedTestCases(newOrderedTestCases);
      
      const payload = {
        project_id: selectedProjectId,
        suite_id: activeTestSuite?.suite_id,
        order: newOrderedTestCases.map((tc) => tc.test_case_id)
      };

      const response = await axiosInstance.put('/test_case/reorder_cases', payload);

      if (response.status === 'success') {
        // Update the main test cases state with the new order
        setTestCases(newOrderedTestCases);
        setTestCasesTotalCount(newOrderedTestCases.length);
        setTestCasesCurrentPage(Math.max(1, Math.ceil(newOrderedTestCases.length / pageSize)));
        setHasMoreTestCases(false);
        setIsReorderMode(false);
        setReorderedTestCases([]);
        toast.success(response?.message || 'Test cases reordered successfully');
      } else {
        throw new Error(response?.message || 'Failed to reorder test cases');
      }
    } catch (error) {
      // Revert to original order on error
      setReorderedTestCases(originalTestCases);
      toast.error(error.response?.data?.message || error.message || 'Failed to reorder test cases');
      console.error('Error reordering test cases:', error);
    } finally {
      stopLoading(requestId);
    }
  };

  const closeCreationModal = (project, skipFetch = false) => {
    if (!skipFetch && project?.project_id) {
      fetctTestSuite(project.project_id, 1, true);
    }
    setIsCreateModalOpen(false);
  }

  
  // Function to handle reordering test suites
  // OLD IMPLEMENTATION: Manual save button approach
  // const handleReorderTestSuites = async () => {
  //   if (isReorderTestSuiteMode) {
  //     try {
  //       const payload = {
  //         project_id: selectedProjectId,
  //         order: reorderedTestSuites.map(suite => suite.suite_id)
  //       };

  //       const response = await axiosInstance.put('/test_suite/reorder_suites', payload);

  //       if (response.status === 'success') {
  //         // Update the test suites state with the new order
  //         setTestSuites(reorderedTestSuites);
  //         setIsReorderTestSuiteMode(false);
  //         showActionFeedback(response.data.message || 'Test suites reordered successfully', 'success');
  //       }
  //     } catch (error) {
  //       showActionFeedback(error.response?.data?.message || 'Failed to reorder test suites', 'error');
  //     }
  //   } else {
  //     setReorderedTestSuites([...testSuites]);
  //     setIsReorderTestSuiteMode(true);
  //   }
  // };

  // NEW IMPLEMENTATION: Auto-save on drop functionality
  const handleReorderTestSuites = async () => {
    if (!selectedProjectId) return;

    let suitesToReorder = [...testSuites];
    const hasAllSuitesLoaded = !hasMoreSuites && testSuites.length >= totalCount;

    if (!hasAllSuitesLoaded) {
      const requestId = `load-all-test-suites-for-reorder-${Date.now()}`;
      startLoading(requestId);
      try {
        suitesToReorder = await fetchAllTestSuitesForReorder(testSuites, currentPage + 1);
      } catch {
        toast.error('Failed to load all test suites for reordering');
        return;
      } finally {
        stopLoading(requestId);
      }
    }

    setReorderedTestSuites(suitesToReorder);
    setIsReorderTestSuiteMode(true);
  };

  // Auto-save function called when test suite order changes
  const handleTestSuiteOrderChange = async (newOrderedTestSuites) => {
    const requestId = `reorder-test-suites-${Date.now()}`;
    startLoading(requestId);
    
    const originalTestSuites = [...reorderedTestSuites];
    
    try {
      // Optimistically update the UI
      setReorderedTestSuites(newOrderedTestSuites);
      
      const payload = {
        project_id: selectedProjectId,
        order: newOrderedTestSuites.map((suite) => suite.suite_id)
      };

      const response = await axiosInstance.put('/test_suite/reorder_suites', payload);

      if (response.status === 'success') {
        // Keep the full reordered list in state (avoid refetching page 1 only)
        setTestSuites(newOrderedTestSuites);
        setTotalCount(newOrderedTestSuites.length);
        setCurrentPage(Math.max(1, Math.ceil(newOrderedTestSuites.length / pageSize)));
        setHasMoreSuites(false);
        setIsReorderTestSuiteMode(false);
        setReorderedTestSuites([]);
        toast.success(response?.message || 'Test suites reordered successfully');
      } else {
        throw new Error(response?.message || 'Failed to reorder test suites');
      }
    } catch (error) {
      // Revert to original order on error
      setReorderedTestSuites(originalTestSuites);
      toast.error(error.response?.data?.message || error.message || 'Failed to reorder test suites');
      console.error('Error reordering test suites:', error);
    } finally {
      stopLoading(requestId);
    }
  };  // Fetch test suites for the selected project from API

  // Set the active test suite when selectedTestSuite is provided
  useEffect(() => {
    if (selectedTestSuite) {
      // Set the selected test suite as active
      setActiveTestSuite(selectedTestSuite);
      
      // Make sure it's in the test suites list if not already
      if (!testSuites.some(suite => suite.id === selectedTestSuite.id)) {
        setTestSuites(prevSuites => [...prevSuites, selectedTestSuite]);
      }
    }
  }, [selectedTestSuite, testSuites]);
  
  // Fetch projects from API and set selected project from query param
  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const response = await axiosInstance.get('/projects/list_project?pro_status=active');
        setProjects(response.projects || []);
        const queryProjectId = searchParams.get("project_id") || projectId;
        const querySuiteId = searchParams.get("suite_id");
        
        if (queryProjectId) {
          setSelectedProjectId(queryProjectId);
        }
        
        if (querySuiteId) {
          setSuiteId(querySuiteId);
        }
      } catch (err) {
        setError('Failed to load projects. Please try again later.');
        setLoading(false);
      }
    };
    fetchProjects();
  }, [projectId, searchParams]);
  
  // Handle running a test case - Opens the RunTestCaseModal
  const handleRunTestCase = (testCaseId) => {
    const testCase = testCases.find(tc => tc.test_case_id === testCaseId);
    if (testCase) {
      setSelectedTestCaseForRun(testCase);
      setIsRunTestCaseModalOpen(true);
    }
  };

  // const handleReorderTestCase = async () => {
  //   if (isReorderMode) {
  //     try {
  //       const payload = {
  //         project_id: selectedProjectId,
  //         suite_id: activeTestSuite?.suite_id,
  //         order: reorderedTestCases.map(tc => tc.test_case_id)
  //       };

  //       const response = await axiosInstance.post('/test_case/reorder_cases', payload);

  //       if (response.data.status === 'success') {
  //         // Update the test cases state with the new order
  //         setTestCases(reorderedTestCases);
  //         setIsReorderMode(false);
  //         showActionFeedback(response.data.message || 'Test cases reordered successfully', 'success');
  //       }
  //     } catch (error) {
  //       showActionFeedback(error.response?.data?.message || 'Failed to reorder test cases', 'error');
  //     }
  //   } else {
  //     setReorderedTestCases([...testCases]);
  //     setIsReorderMode(true);
  //   }
  // };

  const handleViewTestCase = (testCase, projectName) => {
    testCase['projectName'] = projectName;
    setSelectedTestCase(testCase);
    setIsViewTestCaseModalOpen(true);
  };

  const handleEditTestCase = (testCaseId) => {
    const testCase = testCases.find((tc) => tc.test_case_id === testCaseId);
    testCase['project_id'] = selectedProjectId;

    if (testCase) {
      setSelectedTestCase(testCase);
      setIsViewTestCaseModalOpen(false);
      setIsEditTestCaseModalOpen(true);
    }
  };

  const handleSaveTestCase = (updatedTestCase) => {
    // Update the test case in the state
    const updatedTestCases = testCases.map((tc) =>
      tc.test_case_id === updatedTestCase.test_case_id ? updatedTestCase : tc
    );

    setTestCases(updatedTestCases);
    setIsEditTestCaseModalOpen(false);
    showActionFeedback(`Test case "${updatedTestCase.name}" updated successfully`, 'success');
  };

  const handleDeleteTestCase = (testCaseId) => {
    const updatedTestCases = testCases.filter((tc) => tc.id !== testCaseId);
    setTestCases(updatedTestCases);
    setIsViewTestCaseModalOpen(false);
    showActionFeedback(`Test case deleted successfully`, 'success');
  };

  const handleApplySuggestion = (suggestion, testCaseId) => {
    const testCaseToUpdate = testCases.find((tc) => tc.id === testCaseId);
    if (!testCaseToUpdate) return;
    
    const updatedTestCase = {
      ...testCaseToUpdate,
      description: testCaseToUpdate.description
        ? `${testCaseToUpdate.description}\n\nApplied suggestion: ${suggestion.description}`
        : `Applied suggestion: ${suggestion.description}`,
    };

    const updatedTestCases = testCases.map((tc) =>
      tc.id === updatedTestCase.id ? updatedTestCase : tc
    );

    setTestCases(updatedTestCases);
    showActionFeedback(`Applied AI suggestion to test case "${testCaseToUpdate.name}"`, 'success');
  };

    // const handleReorderTestCase = async () => {
    //   if (isReorderMode) {
    //     try {
    //       const payload = {
    //         project_id: selectedProjectId,
    //         suite_id: activeTestSuite?.suite_id,
    //         order: reorderedTestCases.map(tc => tc.test_case_id)
    //       };

    //       const response = await axiosInstance.post('/test_case/reorder_cases', payload);

    //       if (response.data.status === 'success') {
    //         // Update the test cases state with the new order
    //         setTestCases(reorderedTestCases);
    //         setIsReorderMode(false);
    //         showActionFeedback(response.data.message || 'Test cases reordered successfully', 'success');
    //       }
    //     } catch (error) {
    //       showActionFeedback(error.response?.data?.message || 'Failed to reorder test cases', 'error');
    //     }
    //   } else {
    //     setReorderedTestCases([...testCases]);
    //     setIsReorderMode(true);
    //   }
    // };
  // Handle running all test cases in a suite - Opens the RunTestSuiteModal
  const handleRunTestSuite = () => {
    const resolvedSuiteId = suiteId || activeTestSuite?.suite_id;
    if (!resolvedSuiteId) {
      toast.error("No test suite selected");
      return;
    }
    setIsRunTestSuiteModalOpen(true);
  };
  
  // Format date
  const formatDate = (dateString) => formatUserDate(dateString);


const createTestSuit =(isOpen , project)=>{
  setIsCreateModalOpen(isOpen)


  const selectedProject = projects.find(p => p.project_id == projectId || p.project_id == selectedProjectId);
  setTestSuitForSelectedProj(selectedProject || {});
  // setTestSuitForSelectedProj(project)

}
  
  // Calculate test suite statistics
  const calculateStats = (testCases) => {
    const total = testCases.length;
    const passed = testCases.filter(tc => tc.status === 'passed').length;
    const failed = testCases.filter(tc => tc.status === 'failed').length;
    const pending = testCases.filter(tc => tc.status === 'pending').length;
    
    const passRate = total > 0 ? Math.round((passed / total) * 100) : 0;
    
    return { total, passed, failed, pending, passRate };
  };
  
  // Helper for pass rate color
  const getPassRateColor = (rate) => {
    if (rate <= 50) return 'red';
    if (rate > 50 && rate < 75) return 'yellow';
    return 'green';
  };

  if (loading) {
    return null;
  }

  if (error) {
    return (
      <div className="error-container">
        <div className="error-icon">⚠️</div>
        <p>{error}</p>
        <button 
          className="navbar-button primary"
          onClick={() => window.location.reload()}
        >
          Try Again
        </button>
      </div>
    );
  }
  
  if (testSuites.length === 0) {
    return (
      <div className="empty-state">
        <div className="empty-state-icon">
          <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
            <line x1="9" y1="9" x2="15" y2="15"></line>
            <line x1="15" y1="9" x2="9" y2="15"></line>
          </svg>
        </div>
        <h2 className="empty-state-title">No test suites found</h2>
        <p className="empty-state-description">
          There are no test suites available for this project. Create a new test suite to get started.
        </p>
        <button
          className="navbar-button primary inline-flex items-center gap-2"
          // onClick={() => setIsCreateModalOpen(true)}
         onClick={() => createTestSuit(true, selectedProjectId)}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="shrink-0" style={{  marginBottom: 0, marginRight: 0 }}>
            <line x1="12" y1="5" x2="12" y2="19"></line>
            <line x1="5" y1="12" x2="19" y2="12"></line>
          </svg>
          Create Test Suite
        </button>
      </div>
    );
  }
  
  return (
    <div className="test-suites-container" 
    
    // style={{ maxWidth: 1100, margin: '0 auto' }}
    >
      {/* Action Feedback */}
      {actionFeedback && (
        <div className={`action-feedback ${actionFeedback.type}`}>
          <span>{actionFeedback.message}</span>
          <button onClick={() => setActionFeedback(null)}>×</button>
        </div>
      )}
      <div className="test-suites-header">
        <div className="test-suites-header-top">
          <div className="header-with-back">
            {onBack && (
              <button className="back-button" onClick={onBack}>
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="19" y1="12" x2="5" y2="12"></line>
                  <polyline points="12 19 5 12 12 5"></polyline>
                </svg>
                Back to Overview
              </button>
            )}
            <h1 className="test-suites-title">Test Suites</h1>
          </div>
          <div className="test-suites-actions">
            <button
              className="navbar-button secondary"
              onClick={() => handleGenerateAllTestCases()}
              disabled={!selectedProjectId}
              title="Generate automation scripts for all test cases in this project"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'inline-block' }}>
                <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"></path>
              </svg>
              Generate All Test Cases
            </button>
            <button
              className="navbar-button primary inline-flex items-center gap-2"
              onClick={() => createTestSuit(true, selectedProjectId)}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="shrink-0" style={{ display: 'block', marginRight: 0 }}>
                <line x1="12" y1="5" x2="12" y2="19"></line>
                <line x1="5" y1="12" x2="19" y2="12"></line>
              </svg>
              Create Test Suite
            </button>
          </div>
        </div>
        <p className="test-suites-subtitle">Manage and execute your test suites</p>
        {/* <div className="project-filter">
          <label htmlFor="project-filter">Filter by Project:</label>
          <select
            id="project-filter"
            className="form-input"
            value={selectedProjectId || ''}
            onChange={(e) => {
              setSuiteId('');
              setSelectedProjectId(e.target.value || null);
            }}
          >
            <option value="">All Projects</option>
            {projects.map(project => (
              <option key={project.project_id} value={project.project_id}>
                {project.project_name}
              </option>
            ))}
          </select>
        </div> */}
      </div>
      
      <div className="test-suites-content">
        <div className="test-suites-sidebar" style={{ minWidth: 400 }}>
          <div className="test-suites-sidebar-header">
            <h3 className="sidebar-title">Test Suites</h3>
            {!isReorderTestSuiteMode && (
              <button
                className="btn btn-sm btn-secondary"
                onClick={handleReorderTestSuites}
              >
                Reorder Test Suites
              </button>
            )}
          </div>
          <div className="test-suites-sidebar-list" ref={testSuitesListRef}>
            {isReorderTestSuiteMode ? (
              <DraggableList
                items={reorderedTestSuites}
                itemKey="suite_id"
                onReorder={handleTestSuiteOrderChange}
                renderItem={(suite) => (
                  <div className="test-suite-item-wrapper" style={{
                    border: '1px solid #eee',
                    borderRadius: 16,
                    marginBottom: 18,
                    padding: '16px 18px',
                    background: '#fff',
                    cursor: 'move',
                    width: '100%',
                    boxSizing: 'border-box'
                  }}>
                    {/* Drag Handle */}
                    <div className="drag-handle">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M8 6a2 2 0 1 0 0 4 2 2 0 0 0 0-4zm0 8a2 2 0 1 0 0 4 2 2 0 0 0 0-4zm8-8a2 2 0 1 0 0 4 2 2 0 0 0 0-4zm0 8a2 2 0 1 0 0 4 2 2 0 0 0 0-4z"/>
                      </svg>
                    </div>
                    
                    <div className="test-suite-item-content">
                      {/* First row: Name centered */}
                      <div className="test-suite-name-row">
                        <span className="test-suite-name" title={suite.suite_name}>{suite.suite_name}</span>
                        <span className="test-suite-name-count">
                          ({suite.test_case_count} test cases)
                        </span>
                      </div>
                      
                      {/* Second row: Stats */}
                      <div className="test-suite-stats-row">
                        <span
                          className={`pass-rate-badge ${
                            suite.average_pass_rate <= 50
                              ? 'low'
                              : suite.average_pass_rate < 75
                              ? 'medium'
                              : 'high'
                          }`}
                        >
                          {suite.average_pass_rate}%
                        </span>
                        <div className="test-suite-status-chips">
                          <span className="test-suite-stat-chip passed">{suite.passed} passed</span>
                          <span className="test-suite-stat-chip failed">{suite.failed} failed</span>
                          <span className="test-suite-stat-chip pending">{suite.pending} pending</span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              />
            ) : (
              testSuites.map((suite) => {
                const isActive = activeTestSuite && (activeTestSuite.suite_id === suite.suite_id);
                return (
                  <div
                    key={suite.suite_id}
                    className={`test-suite-item-wrapper${isActive ? ' active' : ''}`}
                    style={{
                      border: '1px solid #eee',
                      borderRadius: 16,
                      marginBottom: 18,
                      padding: '16px 18px',
                      background: '#f6f8fa',
                      cursor: 'pointer',
                      width: '100%',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      borderLeft: isActive ? '4px solid #7c3aed' : '4px solid transparent',
                      backgroundColor: isActive ? '#f4f2ff' : '#fff'
                    }}
                    onClick={() => selectTestSuite(suite)}
                  >
                    {/* First row: Name centered */}
                    <div className="test-suite-name-row">
                      <span
                        className="test-suite-name"
                        style={{ fontWeight: 600, fontSize: 18 }}
                        title={suite.suite_name}
                      >
                        {suite.suite_name}
                      </span>
                      <span className="test-suite-name-count">
                        ({suite.test_case_count} test cases)
                      </span>
                    </div>
                    {/* Second row: Pass rate and details */}
                    <div style={{
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between'
                    }}>
                      {/* Pass Rate Pill */}
                      <span
                        style={{
                          background:
                            suite.average_pass_rate <= 50
                              ? '#ffeaea'
                              : suite.average_pass_rate < 75
                              ? '#fffbe6'
                              : '#e6f7e6',
                          color:
                            suite.average_pass_rate <= 50
                              ? '#d32f2f'
                              : suite.average_pass_rate < 75
                              ? '#b59f00'
                              : '#389e3d',
                          borderRadius: 12,
                          padding: '2px 16px',
                          fontWeight: 700,
                          fontSize: 16,
                          minWidth: 54,
                          textAlign: 'center',
                          marginRight: 8,
                          marginLeft: 0,
                          display: 'inline-block'
                        }}
                      >
                        {suite.average_pass_rate}%
                      </span>
              
                      <span style={{
                        background: '#e6f7e6',
                        color: '#389e3d',
                        borderRadius: 8,
                        padding: '2px 8px',
                        fontWeight: 500,
                        fontSize: 13,
                        marginRight: 4
                      }}>
                        {suite.passed} passed
                      </span>
                      <span style={{
                        background: '#ffeaea',
                        color: '#d32f2f',
                        borderRadius: 8,
                        padding: '2px 8px',
                        fontWeight: 500,
                        fontSize: 13,
                        marginRight: 4
                      }}>
                        {suite.failed} failed
                      </span>
                      <span style={{
                        background: '#fffbe6',
                        color: '#b59f00',
                        borderRadius: 8,
                        padding: '2px 8px',
                        fontWeight: 500,
                        fontSize: 13
                      }}>
                        {suite.pending} pending
                      </span>
                    </div>
                  </div>
                );
              })
            )}
            {!isReorderTestSuiteMode && (
              <div ref={loadMoreTriggerRef} style={{ height: 1, width: '100%' }} />
            )}
          </div>
          {!isReorderTestSuiteMode && (
            <>
              {isFetchingMoreSuites && (
                <div style={{ textAlign: 'center', color: '#666', padding: '8px 0 14px' }}>
                  Loading more test suites...
                </div>
              )}
            </>
          )}
        </div>
        
        <div className="test-suite-details" ref={testCasesScrollRef}>
          {activeTestSuite ? (
            <>
              <div className="test-suite-header">
                <div className="test-suite-header-info">
                  <h2 className="test-suite-title" title={activeTestSuite.suite_name}>{activeTestSuite.suite_name}</h2>
                  <p className="test-suite-description">{activeTestSuite.description}</p>
                  <div className="test-suite-meta">
                    <span>Created: {dateFormat(activeTestSuite.created_at)}</span>
                    <span>Last Updated: {dateFormat(activeTestSuite.updated_at)}</span>
                  </div>
                </div>
                <div className="test-suite-actions">
                  {/* <button 
                    className="navbar-button"
                    onClick={() => setIsAddAITestCasesModalOpen(true)}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                      <line x1="8" y1="12" x2="16" y2="12"></line>
                      <line x1="12" y1="8" x2="12" y2="16"></line>
                    </svg>
                    Add AI Test Cases
                  </button> */}
                  <button
                    className="navbar-button primary flex items-center gap-2
             disabled:opacity-50
             disabled:cursor-not-allowed
             disabled:pointer-events-none"
                    onClick={handleRunTestSuite}
                    disabled={testCasesTotalCount === 0 && testCases.length === 0}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polygon points="5 3 19 12 5 21 5 3" />
                    </svg>

                    Run Test Suite
                  </button>
                </div>
              </div>
              <div className="test-cases-container">
                <div className="test-cases-header">
                  <h3 className="test-cases-title">Test Cases</h3>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      className="btn btn-sm btn-primary"
                      onClick={() => setIsCreateTestCaseModalOpen(true)}
                      style={{
                        marginTop: '8px',
                        marginRight: '8px'
                      }}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="12" y1="5" x2="12" y2="19"></line>
                        <line x1="5" y1="12" x2="19" y2="12"></line>
                      </svg>
                      Add Test Case
                    </button>
                    {!isReorderMode && (
                      <button
                        className="btn btn-sm btn-secondary"
                        onClick={() => handleReorderTestCase()}
                      >
                        Reorder Test Cases
                      </button>
                    )}
                  </div>
                </div>
                <div className="test-cases-list">
                  {isLoadingTestCases ? (
                    <div style={{ color: '#666', padding: 16 }}>Loading test cases...</div>
                  ) : testCases.length === 0 ? (
                    <div style={{ color: '#888', padding: 16 }}>No test cases found.</div>
                  ) : isReorderMode ? (
                    <DraggableList
                      items={reorderedTestCases}
                      itemKey="test_case_id"
                      onReorder={handleTestCaseOrderChange}
                      renderItem={(tc) => (
                        <div className="test-case-row" style={{
                          display: 'flex',
                          alignItems: 'center',
                          padding: '12px 0',
                          borderBottom: '1px solid #f0f0f0',
                          flexWrap: 'wrap',
                          cursor: 'move'
                        }}>
                          {/* Drag Handle */}
                          <div className="drag-handle">
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M8 6a2 2 0 1 0 0 4 2 2 0 0 0 0-4zm0 8a2 2 0 1 0 0 4 2 2 0 0 0 0-4zm8-8a2 2 0 1 0 0 4 2 2 0 0 0 0-4zm0 8a2 2 0 1 0 0 4 2 2 0 0 0 0-4z"/>
                            </svg>
                          </div>
                          
                          <span className="test-case-id" style={{ width: 60, color: '#888' }}>#{tc.test_case_id}</span>
                          <span 
                            className="test-case-name" 
                            style={{
                              flex: 1, 
                              fontWeight: 500, 
                              color: '#0366d6', 
                              cursor: 'pointer',
                              textDecoration: 'none',
                              '&:hover': {
                                textDecoration: 'underline'
                              }
                            }}
                          >
                            {tc.name}
                          </span>
                          <span className={`priority-badge priority-${tc.priority.toLowerCase()}`} style={{
                            marginLeft: 8,
                            background: tc.priority === 'high' ? '#ffeaea' : tc.priority === 'medium' ? '#fffbe6' : '#e6f7e6',
                            color: tc.priority === 'high' ? '#d32f2f' : tc.priority === 'medium' ? '#b59f00' : '#389e3d',
                            borderRadius: 4,
                            padding: '2px 8px',
                            fontWeight: 500,
                            fontSize: 13
                          }}>
                            {tc.priority.charAt(0).toUpperCase() + tc.priority.slice(1)}
                          </span>
                          <span className={`status-badge status-${tc.status.toLowerCase()}`} style={{
                            marginLeft: 8,
                            background: tc.status === 'passed' ? '#e6f7e6' : tc.status === 'failed' ? '#ffeaea' : '#fffbe6',
                            color: tc.status === 'passed' ? '#389e3d' : tc.status === 'failed' ? '#d32f2f' : '#b59f00',
                            borderRadius: 4,
                            padding: '2px 8px',
                            fontWeight: 500,
                            fontSize: 13
                          }}>
                            {tc.status.charAt(0).toUpperCase() + tc.status.slice(1)}
                          </span>
                        </div>
                      )}
                    />
                  ) : (
                    testCases.map(tc => (
                      <div key={tc.test_case_id} className="test-case-row" style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '12px 0',
                        borderBottom: '1px solid #f0f0f0',
                        flexWrap: 'wrap'
                      }}>
                        <span className="test-case-id" style={{ width: 60, color: '#888' }}>#{tc.test_case_id}</span>
                        <span className="test-case-name" style={{ flex: 1, fontWeight: 500, color: '#0066cc', cursor: 'pointer' }}
                        onClick={() => handleViewTestCase(tc, tc?.name)}
                        >
                          {tc.name}  <span style={{ color: "black",fontSize: 13  }}>{`${tc.story_key ? `(${tc.story_key})` : ''}`}</span>
                        </span>
                        {/* Action icons */}
                        <span style={{ display: 'flex', alignItems: 'center', gap: 12, marginRight: 16 }}>
                          {/* Run */}
                          <button
                            title="Run"
                            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                            onClick={() => handleRunTestCase(tc.test_case_id)}
                          >
                            <svg width="18" height="18" fill="none" stroke="#7c3aed" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                              <polygon points="5 3 19 12 5 21 5 3"></polygon>
                            </svg>
                          </button>
                          <button
                          // title={tc.script_status ? "Regenerate Test Case " : "Generate Test Case"}
                          style={{ background: 'none', border: 'none', cursor: 'not-allowed', padding: 0 , opacity: 0.5}}
                          onClick={() => handleGenerateTestCase(tc.test_case_id)}
                          disabled={true}
                          title="Regenerate Test Case (Coming Soon)"
                        >
                          <svg width="18" height="18" fill="none" stroke={tc.script_status ? "#389e3d" : "#d32f2f"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                            <path d="M20 11.08V8l-6-6H6a2 2 0 0 0-2 2v16c0 1.1.9 2 2 2h12a2 2 0 0 0 2-2V11.08z"></path>
                            <path d="M14 3v5h5"></path>
                            <path d="M12 15l-3-3 3-3"></path>
                            <path d="M16 15l-3-3 3-3"></path>
                          </svg>
                        </button>
                        <button
                          title="View Script"
                          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                          onClick={() => handleViewScript(tc)}
                        >
                          <svg width="18" height="18" fill="none" stroke="#7c3aed" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                            <path d="M8 3H7a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V5a2 2 0 0 0-2-2h-1"></path>
                            <path d="M12 17v.01"></path>
                            <rect x="12" y="12" width="0.01" height="0.01"></rect>
                            <rect x="12" y="7" width="0.01" height="0.01"></rect>
                          </svg>
                        </button>
                          {/* View */}
                          {/* <button
                            title="View"
                            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                            onClick={() => handleViewTestCase(tc, tc?.name)}
                          >
                            <svg width="18" height="18" fill="none" stroke="#7c3aed" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                              <circle cx="12" cy="12" r="3"></circle>
                              <path d="M1.05 12a10 10 0 0 1 21.9 0 10 10 0 0 1-21.9 0z"></path>
                            </svg>
                          </button> */}
                          {/* Edit */}
                          
                          {/* <button
                            title="Edit"
                            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                          >
                            <svg width="18" height="18" fill="none" stroke="#7c3aed" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                              <path d="M12 20h9"></path>
                              <path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19.5 3 21l1.5-4L16.5 3.5z"></path>
                            </svg>
                          </button> */}
                        </span>
                        <span className={`priority-badge priority-${tc?.priority?.toLowerCase()}`} style={{
                          marginLeft: 8,
                          background: tc?.priority === 'high' ? '#ffeaea' : tc?.priority === 'medium' ? '#fffbe6' : '#e6f7e6',
                          color: tc?.priority === 'high' ? '#d32f2f' : tc?.priority === 'medium' ? '#b59f00' : '#389e3d',
                          borderRadius: 4,
                          padding: '2px 8px',
                          fontWeight: 500,
                          fontSize: 13
                        }}>
                          {tc?.priority?.charAt(0)?.toUpperCase() + tc?.priority?.slice(1)}
                        </span>
                        <span className={`status-badge status-${tc?.status?.toLowerCase()}`} style={{
                          marginLeft: 8,
                          background: tc?.status === 'passed' ? '#e6f7e6' : tc?.status === 'failed' ? '#ffeaea' : '#fffbe6',
                          color: tc?.status === 'passed' ? '#389e3d' : tc?.status === 'failed' ? '#d32f2f' : '#b59f00',
                          borderRadius: 4,
                          padding: '2px 8px',
                          fontWeight: 500,
                          fontSize: 13
                        }}>
                          {tc?.status?.charAt(0)?.toUpperCase() + tc?.status?.slice(1)}
                        </span>
                      </div>
                    ))
                  )}
                  {!isReorderMode && !isLoadingTestCases && (
                    <div ref={loadMoreTestCasesTriggerRef} style={{ height: 1, width: '100%' }} />
                  )}
                </div>
                {!isReorderMode && isFetchingMoreTestCases && (
                  <div style={{ textAlign: 'center', color: '#666', padding: '8px 0 14px' }}>
                    Loading more test cases...
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="test-cases-container">
              <div className="test-cases-header">
                <h3 className="test-cases-title">All Test Cases</h3>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    className="btn btn-sm btn-primary"
                    onClick={() => setIsCreateTestCaseModalOpen(true)}
                    style={{
                      marginTop: '8px',
                      marginRight: '8px'
                    }}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="12" y1="5" x2="12" y2="19"></line>
                      <line x1="5" y1="12" x2="19" y2="12"></line>
                    </svg>
                    Add Test Case
                  </button>
                  {/* {!isReorderMode && (
                    <button
                      className="btn btn-sm btn-secondary"
                      onClick={() => handleReorderTestCase()}
                    >
                      Reorder Test Cases
                    </button>
                  )} */}
                </div>
              </div>
              
              <div className="test-cases-list">
                {isLoadingTestCases ? (
                  <div style={{ color: '#666', padding: 16 }}>Loading test cases...</div>
                ) : testCases.length === 0 ? (
                  <div style={{ color: '#888', padding: 16 }}>No test cases found.</div>
                ) : isReorderMode ? (
                     <DraggableList
      items={reorderedTestCases}
      itemKey="test_case_id"
      onReorder={handleTestCaseOrderChange}
      renderItem={(tc) => (
        <div className="test-case-row" style={{
          display: 'flex',
          alignItems: 'center',
          padding: '12px 0',
          borderBottom: '1px solid #f0f0f0',
          flexWrap: 'wrap',
          cursor: 'move'
        }}>
          {/* Drag Handle */}
          <div className="drag-handle">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d="M8 6a2 2 0 1 0 0 4 2 2 0 0 0 0-4zm0 8a2 2 0 1 0 0 4 2 2 0 0 0 0-4zm8-8a2 2 0 1 0 0 4 2 2 0 0 0 0-4zm0 8a2 2 0 1 0 0 4 2 2 0 0 0 0-4z"/>
            </svg>
          </div>
          
          <span className="test-case-id" style={{ width: 60, color: '#888' }}>#{tc.test_case_id}</span>
          <span className="test-case-name" style={{ flex: 1, fontWeight: 500, color: '#2d3a4a' }}>
            {tc.name}
          </span>
          <span className={`priority-badge priority-${tc.priority.toLowerCase()}`} style={{
            marginLeft: 8,
            background: tc.priority === 'high' ? '#ffeaea' : tc.priority === 'medium' ? '#fffbe6' : '#e6f7e6',
            color: tc.priority === 'high' ? '#d32f2f' : tc.priority === 'medium' ? '#b59f00' : '#389e3d',
            borderRadius: 4,
            padding: '2px 8px',
            fontWeight: 500,
            fontSize: 13
          }}>
            {tc.priority.charAt(0).toUpperCase() + tc.priority.slice(1)}
          </span>
          <span className={`status-badge status-${tc.status.toLowerCase()}`} style={{
            marginLeft: 8,
            background: tc.status === 'passed' ? '#e6f7e6' : tc.status === 'failed' ? '#ffeaea' : '#fffbe6',
            color: tc.status === 'passed' ? '#389e3d' : tc.status === 'failed' ? '#d32f2f' : '#b59f00',
            borderRadius: 4,
            padding: '2px 8px',
            fontWeight: 500,
            fontSize: 13
          }}>
            {tc.status.charAt(0).toUpperCase() + tc.status.slice(1)}
          </span>
        </div>
      )}
    />
  ) : (
                  testCases.map(tc => (
                    <div key={tc.test_case_id} className="test-case-row" style={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: '12px 0',
                      borderBottom: '1px solid #f0f0f0',
                      flexWrap: 'wrap'
                    }}>
                      <span className="test-case-id" style={{ width: 60, color: '#888' }}>#{tc.test_case_id}</span>
                      <span className="test-case-name" style={{ flex: 1, fontWeight: 500, color: '#0066cc', cursor: 'pointer' }}
                      onClick={() => handleViewTestCase(tc, tc?.name)}
                      >
                        {tc.name} <span style={{ color: "black", fontSize: 13 }}>{`${tc.story_key ? `(${tc.story_key})` : ''}`}</span>
                      </span>
                      {/* Action icons */}
                      <span style={{ display: 'flex', alignItems: 'center', gap: 12, marginRight: 16 }}>
                        {/* Run */}
                        <button
                          title="Run"
                          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                          onClick={() => handleRunTestCase(tc.test_case_id)}
                        >
                          <svg width="18" height="18" fill="none" stroke="#7c3aed" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                            <polygon points="5 3 19 12 5 21 5 3"></polygon> 
                          </svg> 
                        </button>
                        <button
                          title={tc.script_status ? "Regenerate Test Case " : "Generate Test Case"}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                          onClick={() => handleGenerateTestCase(tc.test_case_id)}
                        >
                          <svg width="18" height="18" fill="none" stroke={tc.script_status ? "#389e3d" : "#d32f2f"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                            <path d="M20 11.08V8l-6-6H6a2 2 0 0 0-2 2v16c0 1.1.9 2 2 2h12a2 2 0 0 0 2-2V11.08z"></path>
                            <path d="M14 3v5h5"></path>
                            <path d="M12 15l-3-3 3-3"></path>
                            <path d="M16 15l-3-3 3-3"></path>
                          </svg>
                        </button>
                        <button
                          title="View Script"
                          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                          onClick={() => handleViewScript(tc)}
                        >
                          <svg width="18" height="18" fill="none" stroke="#7c3aed" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                            <path d="M8 3H7a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V5a2 2 0 0 0-2-2h-1"></path>
                            <path d="M12 17v.01"></path>
                            <rect x="12" y="12" width="0.01" height="0.01"></rect>
                            <rect x="12" y="7" width="0.01" height="0.01"></rect>
                          </svg>
                        </button>
                        {/* <button
                          title="Edit"
                          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                        >
                          <svg width="18" height="18" fill="none" stroke="#7c3aed" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                            <path d="M12 20h9"></path>
                            <path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19.5 3 21l1.5-4L16.5 3.5z"></path>
                          </svg>
                        </button> */}
                      </span>
                      <span className={`priority-badge priority-${tc.priority.toLowerCase()}`} style={{
                        marginLeft: 8,
                        background: tc.priority === 'high' ? '#ffeaea' : tc.priority === 'medium' ? '#fffbe6' : '#e6f7e6',
                        color: tc.priority === 'high' ? '#d32f2f' : tc.priority === 'medium' ? '#b59f00' : '#389e3d',
                        borderRadius: 4,
                        padding: '2px 8px',
                        fontWeight: 500,
                        fontSize: 13
                      }}>
                        {tc.priority.charAt(0).toUpperCase() + tc.priority.slice(1)}
                      </span>
                      <span className={`status-badge status-${tc.status.toLowerCase()}`} style={{
                        marginLeft: 8,
                        background: tc.status === 'passed' ? '#e6f7e6' : tc.status === 'failed' ? '#ffeaea' : '#fffbe6',
                        color: tc.status === 'passed' ? '#389e3d' : tc.status === 'failed' ? '#d32f2f' : '#b59f00',
                        borderRadius: 4,
                        padding: '2px 8px',
                        fontWeight: 500,
                        fontSize: 13
                      }}>
                        {tc.status.charAt(0).toUpperCase() + tc.status.slice(1)}
                      </span>
                    </div>
                  ))
                )}
                {!isReorderMode && !isLoadingTestCases && (
                  <div ref={loadMoreTestCasesTriggerRef} style={{ height: 1, width: '100%' }} />
                )}
              </div>
              {!isReorderMode && isFetchingMoreTestCases && (
                <div style={{ textAlign: 'center', color: '#666', padding: '8px 0 14px' }}>
                  Loading more test cases...
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      
      {/* Create Test Suite Modal */}
      <CreateTestSuiteModal
        isOpen={isCreateModalOpen}
        onClose={(skipFetch = false) => closeCreationModal(testSuitForSelectedProj, skipFetch)}
        onCreateTestSuite={handleCreateTestSuite}
        projects={testSuitForSelectedProj}
        defaultProjectId={selectedProjectId}
      />
      
      {/* Create Test Case Modal */}
<CreateTestCaseModal
  isOpen={isCreateTestCaseModalOpen}
  onClose={() => setIsCreateTestCaseModalOpen(false)}
  onCreateTestCase={handleCreateTestCase}
  testSuiteId={activeTestSuite?.suite_id}
  projectId={selectedProjectId}
/>
      
      {/* Add AI Test Cases Modal */}
      {activeTestSuite && (
        <AddAITestCasesModal
          isOpen={isAddAITestCasesModalOpen}
          onClose={() => setIsAddAITestCasesModalOpen(false)}
          onAddTestCases={handleAddAITestCases}
          testSuiteId={activeTestSuite.id}
        />
      )}

<ViewTestCaseModal
  isOpen={isViewTestCaseModalOpen}
  onClose={() => setIsViewTestCaseModalOpen(false)}
  testCase={selectedTestCase}
  onEdit={handleEditTestCase}
  onDelete={handleDeleteTestCase}
  onRunTest={handleRunTestCase}
  onApplySuggestion={handleApplySuggestion}
/>

<EditTestCaseModal
  isOpen={isEditTestCaseModalOpen}
  onClose={() => setIsEditTestCaseModalOpen(false)}
  onSaveTestCase={handleSaveTestCase}
  testCase={selectedTestCase}
/>


      {/* View Script Modal */}
      {isViewScriptModalOpen && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000,
          }}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: 8,
              width: '80%',
              height: '80%',
              padding: 24,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
              <h2 style={{ margin: 0 }}>Test Script Editor</h2>
              <button
                onClick={() => setIsViewScriptModalOpen(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                }}
              >
                ×
              </button>
            </div>
            
            <div style={{ flex: 1, border: '1px solid #ccc', borderRadius: 4, position: 'relative' }}>
              <div style={{ position: 'absolute', width: '100%', height: '100%' }}>
                <Editor
                  height="100%"
                  defaultLanguage="python"
                  theme="vs-dark"
                  value={editorContent}
                  onChange={setEditorContent}
                  options={{
                    minimap: { enabled: false },
                    scrollBeyondLastLine: false,
                    fontSize: 14,
                    automaticLayout: true,
                  }}
                  beforeMount={(monaco) => {
                    monaco.editor.defineTheme('customTheme', {
                      base: 'vs-dark',
                      inherit: true,
                      rules: [],
                      colors: {
                        'editor.background': '#1e1e1e',
                      }
                    });
                    monaco.editor.setTheme('customTheme');
                  }}
                />
              </div>
            </div>

            <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button
                onClick={() => setIsViewScriptModalOpen(false)}
                style={{
                  padding: '8px 16px',
                  border: '1px solid #ccc',
                  borderRadius: 4,
                  backgroundColor: 'white',
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSaveScript}
                disabled={isSavingScript}
                style={{
                  padding: '8px 16px',
                  border: 'none',
                  borderRadius: 4,
                  backgroundColor: isSavingScript ? '#9ca3af' : '#7c3aed',
                  color: 'white',
                  cursor: isSavingScript ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                {isSavingScript && (
                  <div style={{
                    width: '16px',
                    height: '16px',
                    border: '2px solid transparent',
                    borderTop: '2px solid white',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }}></div>
                )}
                {isSavingScript ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
          <ConfirmRegenerateModal
        isOpen={isConfirmRegenerateModalOpen}
        onClose={() => {
          setIsConfirmRegenerateModalOpen(false);
          setTestCaseToRegenerate(null);
        }}
        onConfirm={handleConfirmRegenerate}
        testCaseName={testCaseToRegenerate?.name || 'Test Case'}
        isLoading={isRegeneratingScript}
      />
      <RunTestCaseModal
        isOpen={isRunTestCaseModalOpen}
        onClose={() => {
          setIsRunTestCaseModalOpen(false);
          setSelectedTestCaseForRun(null);
        }}
        testCaseId={selectedTestCaseForRun?.test_case_id}
        testCaseName={selectedTestCaseForRun?.name}
        projectId={selectedProjectId}
        projectName={projects.find(p => p.id === selectedProjectId)?.name}
        ai_generated={selectedTestCaseForRun?.automation_status === 'generated'}
        environment={selectedTestCaseForRun?.environment}
        environmentId={selectedTestCaseForRun?.environment_id}
      />
      <RunTestSuiteModal
        isOpen={isRunTestSuiteModalOpen}
        onClose={() => setIsRunTestSuiteModalOpen(false)}
        onSuccess={() => setIsExecutingTestSuite(true)}
        testSuiteId={suiteId || activeTestSuite?.suite_id}
        testSuiteName={activeTestSuite?.suite_name}
        projectId={selectedProjectId}
        environmentId={activeTestSuite?.environment_id}
      />
    </div>
    
  );
};


export default TestSuitesList;